import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  inject,
  computed,
  SimpleChanges,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { RiskGroupShortCodeEnum } from '@olv/ui-shared/enums/risk-group-short-code.enum';
import { WalletDto, CurrencyOption, CurrencyBalances } from '@olv/ui-shared/models/wallet.model';
import {
  MerchantRiskGroup,
  RiskProfileDropdownItem,
  RiskProfileCurrencyBalance,
} from '@olv/ui-shared/models/merchant.model';
import { WithdrawalAvailableBalanceDto } from '@olv/ui-shared/models/sourcing-wallet.model';
import { WithdrawalType, WithdrawalTypeOptions } from '@olv/ui-shared/enums/withdrawal-type.enum';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { MerchantTransactionService } from '@olv/ui-shared/services/merchant-transaction.service';
import { SystemService } from '@olv/ui-shared/services/system.service';
import { BlockchainDropdownComponent } from '@olv/ui-shared/ui-shared/blockchain-dropdown/blockchain-dropdown.component';
import { DropdownComponent } from '@olv/ui-shared/ui-shared/dropdown/dropdown.component';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { TextInputComponent } from '@olv/ui-shared/ui-shared/text-input/text-input.component';
import { catchError, finalize, forkJoin, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { AvailableBalanceComponent } from '@olv/ui-shared/ui-shared/available-balance/available-balance.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

interface ExchangeRate {
  rate: number;
  precision: number;
}

interface CurrencyBalancesResult {
  currencyId: string;
  balances: WithdrawalAvailableBalanceDto[];
}

@Component({
  selector: 'app-merchant-withdraw-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    DropdownComponent,
    BlockchainDropdownComponent,
    TextInputComponent,
    MatIconModule,
    FormContainerComponent,
    AvailableBalanceComponent,
    ButtonComponent
],
  templateUrl: './merchant-withdraw-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./merchant-withdraw-form.component.scss'],
})
export class MerchantWithdrawFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currentStep: 'initial' | 'withdrawal' = 'initial';
  @Input() selectedDestinationWallet: WalletDto | null = null;
  @Input() isSettlementApp = false;
  @Input() initialFormData: any = null;
  @Input() selectedSourceWallet: WalletDto | null = null;
  @Input() totalBalance: CurrencyBalances[] = [];
  @Input() isMultiRiskPaymentsEnabled = false;
  @Input() riskGroups: MerchantRiskGroup[] = [];
  @Input() routingMode: 'automatic' | 'wallet' = 'automatic';
  @Input() merchantId = '';
  @Input() automaticAvailableBalance: number | null = null;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() blockchainChanged = new EventEmitter<number | null>();
  @Output() currencyChanged = new EventEmitter<{ currency: string | null }>();
  @Output() withdrawalTypeChanged = new EventEmitter<string | null>();
  @Output() routingModeChanged = new EventEmitter<'automatic' | 'wallet'>();
  @Output() backClicked = new EventEmitter<void>();

  initialForm!: FormGroup;
  withdrawalForm!: FormGroup;

  withdrawalTypeOptions = WithdrawalTypeOptions;
  readonly blockchainOptions = computed(() => [
    { value: null as number | null, label: 'Select Blockchain' },
    ...this.metadata.networks().map((network) => ({ value: network.networkType as number | null, label: network.name })),
  ]);
  currencyOptions: CurrencyOption[] = [];
  riskGroupOptions: { value: RiskGroupShortCodeEnum; label: string }[] = [];

  exchangeRates: Map<string, ExchangeRate> = new Map();
  baseCurrency = '';
  baseCurrencySymbol = '';
  baseCurrencyPrecision = 0;

  isLoading = false;
  availableBalance: number | null = null;
  currencyPrecision = 2;
  selectedCurrencySymbol = '';

  // Cumulative available for the selected risk group in automatic routing.
  isLoadingAutomaticBalance = false;

  private destroy$ = new Subject<void>();
  private availableBalancesByCurrency = new Map<string, WithdrawalAvailableBalanceDto[]>();
  private readonly inFlightBalanceCurrencies = new Set<string>();
  private balanceRequests$ = new Subject<string | null>();

  private fb = inject(FormBuilder);
  private systemService = inject(SystemService);
  private merchantService = inject(MerchantService);
  private metadata = inject(NetworkMetadataService);
  private transactionService = inject(MerchantTransactionService);
  private formErrorService = inject(FormErrorService);

  constructor() {
    this.initializeForms();
    this.updateSourceWalletValidator();
    this.setupBalanceRequestPipeline();
  }

  ngOnInit(): void {
    this.metadata.load();
    this.loadData();
    this.initializeFormData();
    this.setupFormSubscriptions();
    this.merchantPortalForm();
    this.updateRoutingValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['riskGroups']) {
      this.riskGroupOptions = (this.riskGroups || [])
        .filter((group) => group.fundsClientWithdrawals)
        .map((group) => ({ value: group.shortCode, label: group.label }));

      const selectedRiskGroup = this.initialForm.get('riskGroup')?.value as RiskGroupShortCodeEnum | null;
      if (selectedRiskGroup != null && !this.riskGroupOptions.some((option) => option.value === selectedRiskGroup)) {
        this.initialForm.patchValue({ riskGroup: null });
      }

      this.updateRoutingValidators();
    }

    if (changes['selectedSourceWallet']) {
      const wallet = changes['selectedSourceWallet'].currentValue as WalletDto | null;
      this.setSourceWallet(wallet);
    }

    if (changes['selectedDestinationWallet']) {
      const wallet = changes['selectedDestinationWallet'].currentValue as WalletDto | null;
      this.setDestinationWallet(wallet);
    }

    if (changes['totalBalance']) {
      this.updateAvailableBalanceInfo();
    }

    if (changes['automaticAvailableBalance']) {
      this.updateAvailableBalanceInfo();
      this.withdrawalForm?.get('amount')?.updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get initialNextButtonState(): 'disabled' | 'default' {
    return this.initialForm.invalid || this.isLoading || this.isLoadingAutomaticBalance ? 'disabled' : 'default';
  }

  get withdrawalNextButtonState(): 'disabled' | 'default' {
    return this.withdrawalForm.invalid || !this.selectedDestinationWallet || this.isLoadingAutomaticBalance
      ? 'disabled'
      : 'default';
  }

  private loadData(): void {
    this.isLoading = true;
    forkJoin({
      rates: this.systemService.getExchangeRates(),
    })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ rates }) => {
          this.baseCurrency = rates.baseCurrency;
          this.baseCurrencySymbol = rates.baseCurrencySymbol;
          this.baseCurrencyPrecision = rates.baseCurrencyDisplayPrecision;

          rates.exchangeRates.forEach((r) =>
            this.exchangeRates.set(r.currencySymbol, {
              rate: r.exchangeRateToBase,
              precision: r.currencyDisplayPrecision,
            }),
          );

          this.updateCurrencyOptions();
          this.updateAvailableBalanceInfo();
          this.prefetchAvailableBalances();
        },
        error: (err) => console.error('Form initialization failed', err),
      });
  }

  private initializeForms(): void {
    this.initialForm = this.fb.group({
      blockchain: [null, Validators.required],
      riskGroup: [null],
      sourceWalletDisplay: [null],
      currency: [
        { value: null, disabled: true },
        [Validators.required, ValidationService.currencyBalanceValidator(() => this.getAvailableBalance())],
      ],
    });

    this.withdrawalForm = this.fb.group({
      withdrawalType: [null, this.isSettlementApp ? Validators.required : []],
      destinationWalletDisplay: [null, Validators.required],
      amount: [
        null,
        [
          Validators.required,
          ValidationService.amountValidator(
            () => this.exchangeRates.get(this.initialForm.get('currency')?.value.toUpperCase())?.precision ?? 2,
          ),
          ValidationService.amountBalanceValidator(() => this.getAvailableBalance()),
        ],
      ],
    });
  }

  private merchantPortalForm(): void {
    if (!this.isSettlementApp) {
      if (!this.initialForm.contains('destinationWalletDisplay')) {
        this.initialForm.addControl('destinationWalletDisplay', this.fb.control(null, Validators.required));
      }
      if (!this.initialForm.contains('amount')) {
        this.initialForm.addControl(
          'amount',
          this.fb.control(null, [
            Validators.required,
            ValidationService.amountValidator(
              () => this.exchangeRates.get(this.initialForm.get('currency')?.value?.toUpperCase())?.precision ?? 2,
            ),
            ValidationService.amountBalanceValidator(() => this.getAvailableBalance()),
          ]),
        );
      }
    } else {
      if (!this.initialForm.contains('riskGroup')) {
        this.initialForm.addControl('riskGroup', this.fb.control(null));
      }

      if (this.initialForm.contains('destinationWalletDisplay')) {
        this.initialForm.removeControl('destinationWalletDisplay');
      }
      if (this.initialForm.contains('amount')) {
        this.initialForm.removeControl('amount');
      }
    }
  }

  private updateSourceWalletValidator(): void {
    const control = this.initialForm.get('sourceWalletDisplay');

    if (!control) return;

    if (this.isSettlementApp) {
      if (this.isAutomaticRouting) {
        control.clearValidators();
      } else {
        control.setValidators(Validators.required);
      }
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity();
  }

  private updateRoutingValidators(): void {
    const riskGroup = this.initialForm.get('riskGroup');

    // Risk group drives sourcing for automatic routing, so it is required in both apps when groups exist.
    if (this.isAutomaticRouting && this.riskGroupOptions.length > 0) {
      riskGroup?.setValidators(Validators.required);
    } else {
      riskGroup?.clearValidators();
    }

    riskGroup?.updateValueAndValidity();
    this.updateSourceWalletValidator();
  }

  private initializeFormData(): void {
    if (this.currentStep === 'withdrawal' && this.initialFormData) {
      this.initialForm.patchValue({
        blockchain: this.initialFormData.blockchain,
        currency: this.initialFormData.currency,
      });

      this.updateSourceWalletDisplay();
      this.updateAvailableBalanceInfo();

      if (!this.isSettlementApp) {
        this.withdrawalForm.patchValue({ withdrawalType: WithdrawalType.ClientPayout });
        this.withdrawalTypeChanged.emit(String(WithdrawalType.ClientPayout));
      }
    } else if (this.currentStep !== 'withdrawal' && this.initialFormData) {
      this.routingMode = this.initialFormData.routingMode ?? this.routingMode;

      this.initialForm.patchValue({
        blockchain: this.initialFormData.blockchain,
        currency: this.initialFormData.currency,
        riskGroup: this.initialFormData.riskGroup ?? null,
      });

      this.currencyOptions = this.initialFormData.currencyOptions;
      this.initialForm.get('currency')?.enable();
      this.updateSourceWalletDisplay();
      this.updateAvailableBalanceInfo();
    } else {
      this.onResetForm();
    }
  }

  private setupFormSubscriptions(): void {
    this.initialForm
      .get('blockchain')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleBlockchainChange());

    this.initialForm
      .get('currency')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleCurrencyChange());

    this.withdrawalForm
      .get('withdrawalType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleWithdrawalTypeChange());

    this.initialForm
      .get('riskGroup')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadAutomaticAvailableBalance());
  }

  private loadAutomaticAvailableBalance(): void {
    if (this.isSettlementApp) {
      return;
    }

    if (this.currentStep === 'withdrawal') {
      return;
    }

    const riskGroup = this.initialForm.get('riskGroup')?.value as RiskGroupShortCodeEnum | null;
    const currencyId = this.getCurrencyId();

    if (riskGroup == null || !currencyId) {
      this.balanceRequests$.next(null);
      this.automaticAvailableBalance = null;
      this.updateAvailableBalanceInfo();
      this.revalidateAmountControl();
      return;
    }

    const cached = this.availableBalancesByCurrency.get(currencyId);
    if (cached) {
      this.applyAvailableBalances(currencyId, cached);
      return;
    }

    // switchMap in the pipeline drops any earlier in-flight response so a slow stale
    // request can never overwrite the balance for the latest selection.
    this.balanceRequests$.next(currencyId);
  }

  private prefetchAvailableBalances(): void {
    if (this.isSettlementApp) {
      return;
    }

    const currencyIds = this.currencyOptions
      .map((option) => option.id)
      .filter(
        (id): id is string =>
          !!id && !this.availableBalancesByCurrency.has(id) && !this.inFlightBalanceCurrencies.has(id),
      );

    if (currencyIds.length === 0) {
      return;
    }

    currencyIds.forEach((currencyId) => this.inFlightBalanceCurrencies.add(currencyId));
    this.isLoadingAutomaticBalance = true;
    forkJoin(
      currencyIds.map((currencyId) =>
        this.transactionService.getAutomaticWithdrawalAvailableBalances(currencyId).pipe(
          map((balances) => ({ currencyId, balances }) as CurrencyBalancesResult),
          catchError((err) => {
            console.error('Error loading automatic available balances:', err);
            this.clearBalanceIfStillSelected(currencyId);
            return of<CurrencyBalancesResult | null>(null);
          }),
          finalize(() => this.inFlightBalanceCurrencies.delete(currencyId)),
        ),
      ),
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingAutomaticBalance = false)),
      )
      .subscribe((results) => {
        results.forEach((result) => {
          if (result) {
            this.availableBalancesByCurrency.set(result.currencyId, result.balances);
          }
        });

        const selectedCurrencyId = this.getCurrencyId();
        const selected = selectedCurrencyId ? this.availableBalancesByCurrency.get(selectedCurrencyId) : undefined;
        if (selectedCurrencyId && selected) {
          this.applyAvailableBalances(selectedCurrencyId, selected);
        }
      });
  }

  private clearBalanceIfStillSelected(currencyId: string): void {
    if (this.getCurrencyId() !== currencyId) {
      return;
    }

    this.automaticAvailableBalance = null;
    this.updateAvailableBalanceInfo();
    this.revalidateAmountControl();
  }

  private applyAvailableBalances(currencyId: string, balances: WithdrawalAvailableBalanceDto[]): void {
    if (this.getCurrencyId() !== currencyId) {
      return;
    }

    const riskGroup = this.initialForm.get('riskGroup')?.value as RiskGroupShortCodeEnum | null;
    if (riskGroup == null) {
      this.automaticAvailableBalance = null;
    } else {
      const match = balances.find((b) => b.riskGroupShortCode === riskGroup);
      this.automaticAvailableBalance = match?.cumulativeAvailable ?? 0;
    }

    this.updateAvailableBalanceInfo();
    this.revalidateAmountControl();
  }

  private setupBalanceRequestPipeline(): void {
    this.balanceRequests$
      .pipe(
        takeUntil(this.destroy$),
        switchMap((currencyId) => {
          if (!currencyId) {
            this.isLoadingAutomaticBalance = false;
            return of<CurrencyBalancesResult | null>(null);
          }

          this.isLoadingAutomaticBalance = true;
          return this.transactionService
            .getAutomaticWithdrawalAvailableBalances(currencyId, this.isSettlementApp ? this.merchantId : undefined)
            .pipe(
              map((balances) => ({ currencyId, balances }) as CurrencyBalancesResult),
              catchError((err) => {
                console.error('Error loading automatic available balances:', err);
                this.clearBalanceIfStillSelected(currencyId);
                return of<CurrencyBalancesResult | null>(null);
              }),
              finalize(() => (this.isLoadingAutomaticBalance = false)),
            );
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.availableBalancesByCurrency.set(result.currencyId, result.balances);
        this.applyAvailableBalances(result.currencyId, result.balances);
      });
  }

  private revalidateAmountControl(): void {
    this.initialForm?.get('amount')?.updateValueAndValidity();
    this.withdrawalForm?.get('amount')?.updateValueAndValidity();
  }

  calculateExchange(amount: number | string, currency: string | null): number {
    if (!currency) return 0;

    const symbolCurrency = currency.toUpperCase();
    const numberAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    const rate = this.exchangeRates.get(symbolCurrency)?.rate;

    if (!rate) return 0;

    return numberAmount * rate;
  }

  setSourceWallet(wallet: WalletDto | null): void {
    this.selectedSourceWallet = wallet;

    if (wallet) {
      this.initialForm.patchValue({ blockchain: wallet.networkType }, { emitEvent: false });
      this.initialForm.get('currency')?.enable();
      this.updateCurrencyOptions(wallet);
      this.initialForm.get('currency')?.updateValueAndValidity();
    } else if (this.isSettlementApp && this.isAutomaticRouting && this.initialForm.get('blockchain')?.value != null) {
      this.initialForm.get('currency')?.enable();
      this.updateCurrencyOptions();
    } else {
      this.initialForm.get('currency')?.disable();
    }

    this.updateSourceWalletDisplay();
    this.updateAvailableBalanceInfo();
  }

  setDestinationWallet(wallet: WalletDto | null): void {
    this.selectedDestinationWallet = wallet;

    if (!this.isSettlementApp && wallet && this.currentStep === 'initial') {
      const currentBlockchain = this.initialForm.get('blockchain')?.value;

      if (currentBlockchain !== wallet.networkType) {
        this.initialForm.patchValue({ blockchain: wallet.networkType });
      }
    }

    this.updateDestinationWalletDisplay();
  }

  private handleBlockchainChange(): void {
    const currencyControl = this.initialForm.get('currency');
    currencyControl?.reset();
    currencyControl?.disable();

    this.currencyOptions = [];
    this.updateSourceWalletDisplay();

    const blockchain = this.initialForm.get('blockchain')?.value;
    this.blockchainChanged.emit(blockchain);

    if (this.isAutomaticRouting && blockchain != null) {
      currencyControl?.enable();
      this.updateCurrencyOptions();
      return;
    }

    // Merchant Portal: no source wallet needed, populate currency directly
    if (!this.isSettlementApp && blockchain != null) {
      currencyControl?.enable();
      this.updateCurrencyOptions();
    }
  }

  private handleCurrencyChange(): void {
    const currency = this.initialForm.get('currency')?.value;
    this.updateAvailableBalanceInfo();
    this.prefetchAvailableBalances();
    this.loadAutomaticAvailableBalance();
    this.currencyChanged.emit({ currency });
  }

  private handleWithdrawalTypeChange(): void {
    this.updateDestinationWalletDisplay();

    this.withdrawalTypeChanged.emit(this.withdrawalForm.get('withdrawalType')?.value);
  }

  private updateCurrencyOptions(wallet?: WalletDto): void {
    const blockchain = this.initialForm.get('blockchain')?.value;
    const currencyControl = this.initialForm.get('currency');

    if (!currencyControl || blockchain == null) {
      this.currencyOptions = [];
      currencyControl?.reset();
      return;
    }

    if (currencyControl.disabled) {
      return;
    }

    this.currencyOptions = this.metadata.currencyOptionsOf(blockchain);

    // On the withdrawal step the currency is finalized from the initial step — only refresh options, never re-default.
    if (this.currentStep === 'withdrawal') {
      return;
    }

    const tokenOption = this.currencyOptions.find((c) => !this.metadata.currency(c.id)?.isNative);

    if (this.isAutomaticRouting) {
      this.currencyOptions = tokenOption ? [tokenOption] : [];
      const defaultCurrency = tokenOption?.value ?? null;
      this.initialForm.patchValue({ currency: defaultCurrency });
      this.currencyChanged.emit({ currency: defaultCurrency });
      return;
    }

    let hasTokenBalance = true;

    if (wallet && tokenOption) {
      hasTokenBalance = wallet.balance.currencyBalances.some(
        (cb) => cb.currencySymbol.toUpperCase() === tokenOption.label.toUpperCase() && cb.totalAmountInCurrency > 0,
      );
    }

    let defaultCurrency =
      hasTokenBalance && tokenOption
        ? tokenOption.value
        : this.nativeCurrencyValue(blockchain);

    if (!this.isSettlementApp) defaultCurrency = tokenOption?.value ?? this.currencyOptions[0]?.value ?? null;

    this.initialForm.patchValue({ currency: defaultCurrency });
    this.currencyChanged.emit({ currency: defaultCurrency });
  }

  private nativeCurrencyValue(networkType: number): string | null {
    return this.metadata.nativeCurrencyOf(networkType)?.symbol.toLowerCase() ?? null;
  }

  private updateSourceWalletDisplay(): void {
    const displayValue = this.selectedSourceWallet ? this.getWalletDisplayName(this.selectedSourceWallet) : '';

    this.initialForm.patchValue({ sourceWalletDisplay: displayValue });
  }

  private updateDestinationWalletDisplay(): void {
    const displayValue = this.selectedDestinationWallet
      ? this.getWalletDisplayName(this.selectedDestinationWallet)
      : '';

    if (!this.isSettlementApp && this.currentStep === 'initial') {
      this.initialForm.patchValue({ destinationWalletDisplay: displayValue });
    } else {
      this.withdrawalForm.patchValue({ destinationWalletDisplay: displayValue });
    }
  }

  private getWalletDisplayName(wallet: WalletDto): string {
    const name = wallet.walletName || `Wallet ${wallet.id.substring(0, 6)}`;
    const address = wallet.address
      ? `${wallet.address.substring(0, 10)}...${wallet.address.substring(wallet.address.length - 10)}`
      : 'No address';
    return `${name} (${address})`;
  }

  onSubmit(): void {
    const activeForm = this.currentStep === 'withdrawal' ? this.withdrawalForm : this.initialForm;

    if (activeForm.invalid) {
      return;
    }

    if (this.currentStep === 'initial' || !this.isSettlementApp) {
      const currencyId = this.getCurrencyId();
      if (!currencyId || !ValidationService.isValidGuid(currencyId)) {
        return;
      }
    }

    const submissionData =
      this.currentStep === 'withdrawal' ? this.buildSubmissionWithdrawal() : this.buildSubmissionInitial();
    this.formSubmit.emit(submissionData);
  }

  private buildSubmissionInitial(): any {
    const currency = this.initialForm.get('currency')?.value;
    const blockchain = this.initialForm.get('blockchain')?.value;

    if (!this.isSettlementApp) {
      const amount = this.initialForm.get('amount')?.value;
      return {
        blockchain: blockchain,
        currency: currency,
        currencyId: this.getCurrencyId(),
        currencyOptions: this.currencyOptions,
        withdrawalType: WithdrawalType.ClientPayout,
        receiverAddress: this.selectedDestinationWallet?.address,
        receiverWalletId: this.selectedDestinationWallet?.id,
        amount: amount,
        riskGroup: this.initialForm.get('riskGroup')?.value ?? null,
        exchangeRate: this.exchangeRates.get(currency?.toUpperCase() ?? '')?.rate,
        convertedAmount: this.calculateExchange(amount, currency),
        baseCurrencyPrecision: this.baseCurrencyPrecision,
        baseCurrencySymbol: this.baseCurrency,
        currencyPrecision: this.exchangeRates.get(currency?.toUpperCase())?.precision ?? 2,
      };
    }

    return {
      blockchain: blockchain,
      currency: currency,
      currencyId: this.getCurrencyId(),
      currencyOptions: this.currencyOptions,
      routingMode: this.routingMode,
      riskGroup: this.initialForm.get('riskGroup')?.value,
    };
  }

  private buildSubmissionWithdrawal(): any {
    const currency = this.initialForm.get('currency')?.value;
    const amount = this.withdrawalForm.get('amount')?.value;

    return {
      withdrawalType: this.withdrawalForm.get('withdrawalType')?.value,
      receiverAddress: this.selectedDestinationWallet?.address,
      receiverWalletId: this.selectedDestinationWallet?.id,
      amount: amount,
      exchangeRate: this.exchangeRates.get(currency?.toUpperCase() ?? '')?.rate,
      convertedAmount: this.calculateExchange(amount, currency),
      baseCurrencyPrecision: this.baseCurrencyPrecision,
      baseCurrencySymbol: this.baseCurrency,
      currencyPrecision: this.exchangeRates.get(this.initialForm.get('currency')?.value.toUpperCase())?.precision ?? 2,
    };
  }

  private getAvailableBalance(): number | null {
    // Automatic withdrawal: cumulative available for the selected risk group (both apps).
    if (this.isAutomaticWithdrawal) {
      return this.automaticAvailableBalance;
    }

    // Merchant Portal: use totalBalance with available amount
    if (!this.isSettlementApp && this.initialForm.get('blockchain')?.value) {
      const currency = this.initialForm.get('currency')?.value;
      const currencyId = this.currencyOptions.find((c) => c.value === currency)?.id;

      const balance = this.totalBalance.find((b) => b.currencyId === currencyId);

      return balance?.availableAmountInCurrency ?? null;
    }

    if (!this.selectedSourceWallet || !this.initialForm.get('currency')?.value) {
      return null;
    }

    const balance = this.selectedSourceWallet.balance?.currencyBalances.find(
      (b) => b.currencySymbol?.toLowerCase() === this.initialForm.get('currency')?.value.toLowerCase(),
    );

    return balance?.availableAmountInCurrency ?? null;
  }

  getCurrencyId(): string | null {
    const currency = this.initialForm.get('currency')?.value;
    if (!currency) return null;

    // Merchant Portal: get currency ID from currency options
    if (!this.isSettlementApp) {
      const option = this.currencyOptions.find((c) => c.value.toLowerCase() === currency.toLowerCase());
      return option?.id ?? null;
    }

    if (this.isAutomaticRouting) {
      const option = this.currencyOptions.find((c) => c.value.toLowerCase() === currency.toLowerCase());
      return option?.id ?? null;
    }

    // Settlement App: get from source wallet balance
    if (!this.selectedSourceWallet) return null;

    const balance = this.selectedSourceWallet.balance.currencyBalances.find(
      (b) => b.currencySymbol?.toLowerCase() === currency.toLowerCase(),
    );

    return balance?.currencyId || null;
  }

  onResetForm(): void {
    this.initialForm.reset();
    this.withdrawalForm.reset();
    this.currencyOptions = [];
  }

  updateAvailableBalanceInfo(): void {
    this.availableBalance = this.getAvailableBalance();
    const currency = this.initialForm.get('currency')?.value;
    if (currency) {
      this.selectedCurrencySymbol = currency.toUpperCase();
      this.currencyPrecision = this.exchangeRates.get(currency.toUpperCase())?.precision ?? 2;
    } else {
      this.selectedCurrencySymbol = '';
      this.currencyPrecision = 2;
    }
  }

  getFieldError(fieldName: string, label: string, customMessages?: { [key: string]: string }): string {
    const form = this.currentStep === 'withdrawal' ? this.withdrawalForm : this.initialForm;
    const control = form.get(fieldName);

    return this.formErrorService.getErrorMessage(control, label, customMessages);
  }

  onBackClick(): void {
    this.backClicked.emit();
    this.withdrawalForm.reset();
  }

  get isAutomaticRouting(): boolean {
    return this.routingMode === 'automatic';
  }

  get isAutomaticWithdrawal(): boolean {
    return this.isSettlementApp ? this.isAutomaticRouting : this.initialForm.get('riskGroup')?.value != null;
  }

  selectRoutingMode(mode: 'automatic' | 'wallet'): void {
    if (this.routingMode === mode) {
      return;
    }

    this.routingMode = mode;
    this.routingModeChanged.emit(mode);

    this.initialForm.patchValue({ riskGroup: null });

    if (mode === 'automatic') {
      this.initialForm.patchValue({ sourceWalletDisplay: null });
    } else {
      this.updateSourceWalletDisplay();
    }

    if (this.isAutomaticRouting) {
      this.selectedSourceWallet = null;
    }

    this.updateRoutingValidators();
    const hasBlockchain = this.initialForm.get('blockchain')?.value != null;
    const currencyControl = this.initialForm.get('currency');

    if (!hasBlockchain) {
      this.currencyOptions = [];
      this.initialForm.get('currency')?.reset();
      this.initialForm.get('currency')?.disable();
    } else {
      currencyControl?.enable();
      this.updateCurrencyOptions();
    }

    this.updateAvailableBalanceInfo();
  }
}
